import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { RefreshCcw, ExternalLink, CheckCircle2, XCircle, Clock, AlertCircle, GitCommit } from 'lucide-react';
import { useTestRuns } from '@/hooks/useTestRuns';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import TestStatistics from './TestStatistics';
import TestResults from './TestResults';
import TestCoverageChart from './TestCoverageChart';
import { CurrentCIStatus } from './CurrentCIStatus';

const TestDashboard = () => {
  const { runs, selectedRun, setSelectedRun, testResults, isLoading, error, refetch } = useTestRuns();

  const openGitHubWorkflow = () => {
    if (selectedRun?.workflow_run_id) {
      // TODO: Update with actual GitHub repository URL
      const repoUrl = 'https://github.com/YOUR_ORG/YOUR_REPO';
      window.open(`${repoUrl}/actions/runs/${selectedRun.workflow_run_id}`, '_blank');
    }
  };

  const openDatadogTrace = () => {
    if (selectedRun?.datadog_trace_id) {
      window.open(`https://app.datadoghq.com/apm/trace/${selectedRun.datadog_trace_id}`, '_blank');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <Clock className="h-4 w-4 text-yellow-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      passed: 'default',
      failed: 'destructive',
      running: 'secondary',
      error: 'destructive'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  // Convert test results to the format expected by TestResults component
  const formattedResults = selectedRun ? {
    totalTests: selectedRun.total_tests,
    passed: selectedRun.passed,
    failed: selectedRun.failed,
    skipped: selectedRun.skipped,
    duration: selectedRun.duration || 0,
    timestamp: selectedRun.created_at,
    tests: testResults.map(t => ({
      name: t.test_name,
      status: t.status,
      duration: t.duration || 0,
      file: t.test_file,
      error: t.error_message || undefined
    }))
  } : null;

  const formattedCoverage = selectedRun ? {
    lines: selectedRun.coverage_lines || 0,
    statements: selectedRun.coverage_statements || 0,
    functions: selectedRun.coverage_functions || 0,
    branches: selectedRun.coverage_branches || 0
  } : null;

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard de Testes CI/CD</h1>
          <p className="text-muted-foreground mt-1">
            Status atual do CI/CD e histórico de execuções via GitHub Actions
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={refetch} variant="outline" disabled={isLoading}>
            <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          {selectedRun?.workflow_run_id && (
            <Button onClick={openGitHubWorkflow} variant="outline">
              <ExternalLink className="h-4 w-4 mr-2" />
              GitHub Actions
            </Button>
          )}
          {selectedRun?.datadog_trace_id && (
            <Button onClick={openDatadogTrace} variant="outline">
              <ExternalLink className="h-4 w-4 mr-2" />
              Datadog Trace
            </Button>
          )}
        </div>
      </div>

      {/* Current CI Status */}
      <CurrentCIStatus />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar dados</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* No Runs Warning */}
      {!isLoading && runs.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Nenhuma execução encontrada</AlertTitle>
          <AlertDescription>
            Ainda não há execuções de testes registradas. Configure o GitHub Actions para começar a registrar resultados.
          </AlertDescription>
        </Alert>
      )}

      {/* Commit Selector */}
      {runs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitCommit className="h-5 w-5" />
              Histórico de Execuções de Teste
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedRun?.id}
              onValueChange={(id) => {
                const run = runs.find(r => r.id === id);
                if (run) setSelectedRun(run);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione uma execução..." />
              </SelectTrigger>
              <SelectContent>
                {runs.map(run => (
                  <SelectItem key={run.id} value={run.id}>
                    <div className="flex items-center gap-3 py-1">
                      {getStatusIcon(run.status)}
                      
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm">
                            {run.commit_sha.substring(0, 7)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {run.branch_name}
                          </Badge>
                          {getStatusBadge(run.status)}
                        </div>
                        <span className="text-xs text-muted-foreground truncate">
                          {run.commit_message?.substring(0, 60)}
                          {run.commit_message && run.commit_message.length > 60 ? '...' : ''}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {run.author_name} • {format(new Date(run.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      {selectedRun && formattedResults && (
        <>
          <TestStatistics 
            results={formattedResults}
            isRunning={selectedRun.status === 'running'}
          />

          {formattedCoverage && (
            <TestCoverageChart coverage={formattedCoverage} />
          )}

          <TestResults 
            results={formattedResults}
            isRunning={selectedRun.status === 'running'}
          />
        </>
      )}

      {/* Info Card */}
      {!selectedRun && runs.length === 0 && !isLoading && (
        <Card className="p-6 bg-card/50 border-border">
          <div className="text-center text-muted-foreground">
            <p className="text-lg mb-2">Configure o Pipeline de CI/CD</p>
            <p className="text-sm">
              Configure o GitHub Actions para começar a registrar resultados de testes automaticamente.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default TestDashboard;
